// src/components/ExportToLibraryModal.jsx
import React, { useState } from "react";
import { Filter } from "bad-words";
import { AutocompleteInput } from "./AutocompleteInput";
import {
  INSTRUMENTATIONS,
  GENRES,
  POPULAR_COMPOSERS,
  COMMON_TAGS,
} from "../data/musicData";

function ExportToLibraryModal({
  exportToLibraryPattern,
  setExportToLibraryPattern,
  exportPatternToLibrary,
  patternTags,
  setPatternTags,
}) {
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  // Create a local copy of the patternTags state
  const [localTags, setLocalTags] = useState({
    composer: patternTags.composer || "",
    instrumentation: patternTags.instrumentation || "",
    genre: patternTags.genre || "",
    custom: [], // New array for custom tags
  });
  const [newCustomTag, setNewCustomTag] = useState("");

  const handleAddCustomTag = () => {
    if (!newCustomTag.trim()) return;

    // Check for profanity
    const filter = new Filter();
    if (filter.isProfane(newCustomTag)) {
      setError("Please use appropriate language in your tags");
      return;
    }

    // Limit to 3 custom tags
    if (localTags.custom.length >= 3) {
      setError("Maximum of 3 custom tags allowed");
      return;
    }

    // Check if tag already exists
    if (localTags.custom.includes(newCustomTag.trim())) {
      setError("This tag already exists");
      return;
    }

    // Add the new tag
    setLocalTags({
      ...localTags,
      custom: [...localTags.custom, newCustomTag.trim()],
    });
    setNewCustomTag("");
    setError("");
  };

  const handleRemoveCustomTag = (tagToRemove) => {
    setLocalTags({
      ...localTags,
      custom: localTags.custom.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleExportToLibrary = async () => {
    const filter = new Filter();
    if (description && filter.isProfane(description)) {
      setError("Please use appropriate language in your description");
      return;
    }

    // Update the global patternTags state
    setPatternTags(localTags);

    // Create the full pattern object with description and tags
    const enrichedPattern = {
      ...exportToLibraryPattern,
      description,
      tags: localTags,
    };

    // Call the export function
    exportPatternToLibrary(enrichedPattern);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Share to FlowFrame Library</h2>

        {error && (
          <div className="mb-4 p-2 bg-red-50 text-red-700 rounded">{error}</div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Pattern Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Briefly describe your pattern..."
            className="w-full p-2 border rounded h-24"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Composer</label>
          <AutocompleteInput
            value={localTags.composer}
            onChange={(e) =>
              setLocalTags({ ...localTags, composer: e.target.value })
            }
            suggestions={POPULAR_COMPOSERS}
            placeholder="e.g., Bach, Beethoven, etc."
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Instrumentation
          </label>
          <select
            name="instrumentation"
            value={localTags.instrumentation}
            onChange={(e) =>
              setLocalTags({ ...localTags, instrumentation: e.target.value })
            }
            className="w-full p-2 border rounded"
          >
            <option value="">Select instrumentation...</option>
            {INSTRUMENTATIONS.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Genre</label>
          <select
            name="genre"
            value={localTags.genre}
            onChange={(e) =>
              setLocalTags({ ...localTags, genre: e.target.value })
            }
            className="w-full p-2 border rounded"
          >
            <option value="">Select genre...</option>
            {GENRES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        {/* Custom Tags Section */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Custom Tags (max 3)
          </label>
          <div className="flex">
            <AutocompleteInput
              value={newCustomTag}
              onChange={(e) => setNewCustomTag(e.target.value)}
              suggestions={COMMON_TAGS}
              placeholder="Add a custom tag..."
              className="flex-1 p-2 border rounded-l"
            />
            <button
              onClick={handleAddCustomTag}
              className="px-3 py-2 bg-gray-200 rounded-r border-t border-r border-b"
              disabled={localTags.custom.length >= 3}
            >
              Add
            </button>
          </div>
          {localTags.custom.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {localTags.custom.map((tag) => (
                <div
                  key={tag}
                  className="flex items-center bg-gray-100 text-gray-800 px-2 py-1 rounded"
                >
                  <span className="mr-1">{tag}</span>
                  <button
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => handleRemoveCustomTag(tag)}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {3 - localTags.custom.length} tags remaining
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setExportToLibraryPattern(null)}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleExportToLibrary}
            className="px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700"
          >
            Share to Library
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportToLibraryModal;
